import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye } from 'lucide-react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { SeoMeta } from '../components/SeoMeta';
import { api } from '../lib/api';

function PasswordField({
  id,
  value,
  onChange,
  reveal,
  onRevealChange,
}: {
  id: string;
  value: string;
  onChange: (v: string) => void;
  reveal: boolean;
  onRevealChange: (v: boolean) => void;
}) {
  return (
    <div className="relative">
      <input
        id={id}
        type={reveal ? 'text' : 'password'}
        required
        minLength={8}
        value={value}
        onChange={(ev) => onChange(ev.target.value)}
        className="w-full rounded-lg border border-border py-2 pl-3 pr-11 text-text"
        autoComplete="new-password"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Wachtwoord tonen (ingedrukt houden)"
        className="absolute right-1 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-md text-text-muted hover:bg-surface hover:text-text"
        onPointerDown={(e) => {
          e.preventDefault();
          onRevealChange(true);
        }}
        onPointerUp={() => onRevealChange(false)}
        onPointerLeave={() => onRevealChange(false)}
        onPointerCancel={() => onRevealChange(false)}
      >
        <Eye className="h-4 w-4 shrink-0" aria-hidden />
      </button>
    </div>
  );
}

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token')?.trim() ?? '';
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [ok, setOk] = useState(false);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== password2) {
      setError('Wachtwoorden komen niet overeen.');
      return;
    }
    if (!token) {
      setError('Ongeldige of ontbrekende link. Vraag een nieuwe reset aan.');
      return;
    }
    setLoading(true);
    try {
      await api.authResetPassword({ token, password });
      setOk(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-page-enter">
      <SeoMeta
        path="/reset-password"
        title="Nieuw wachtwoord"
        description="Stel een nieuw wachtwoord in voor je CalqTool™-account via de link uit je e-mail. Sterk wachtwoord aanbevolen — daarna kun je weer inloggen."
      />
      <AnimatedSection delay={0.05}>
        <Breadcrumbs items={[{ label: 'Home', href: '/' }, { label: 'Nieuw wachtwoord' }]} />
        <div className="mx-auto max-w-md rounded-lg border border-border bg-surface-card p-8">
          <h1 className="mb-1 text-2xl font-bold text-text">Nieuw wachtwoord</h1>
          <p className="mb-6 text-sm text-text-muted">Kies een sterk wachtwoord (minimaal 8 tekens).</p>

          {!token && !ok && (
            <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/40 dark:text-amber-100" role="alert">
              Deze pagina heeft een geldige token uit de e-mail nodig.{' '}
              <Link to="/forgot-password" className="font-medium text-primary hover:underline">
                Vraag een nieuwe link aan
              </Link>
              .
            </p>
          )}

          {ok && (
            <p className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm text-text" role="status">
              Wachtwoord bijgewerkt. Je wordt doorgestuurd naar inloggen…
            </p>
          )}

          {!ok && token && (
            <form onSubmit={(e) => void onSubmit(e)} className="space-y-4">
              <div>
                <label htmlFor="reset-pw" className="mb-1 block text-sm font-medium text-text">
                  Nieuw wachtwoord
                </label>
                <PasswordField id="reset-pw" value={password} onChange={setPassword} reveal={reveal} onRevealChange={setReveal} />
              </div>
              <div>
                <label htmlFor="reset-pw2" className="mb-1 block text-sm font-medium text-text">
                  Bevestig wachtwoord
                </label>
                <input
                  id="reset-pw2"
                  type={reveal ? 'text' : 'password'}
                  required
                  minLength={8}
                  value={password2}
                  onChange={(ev) => setPassword2(ev.target.value)}
                  className="w-full rounded-lg border border-border px-3 py-2 text-text"
                  autoComplete="new-password"
                />
              </div>
              {error && (
                <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100" role="alert">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg bg-primary py-3 font-medium text-white transition-all hover:bg-primary-alt disabled:opacity-60"
              >
                {loading ? 'Bezig…' : 'Wachtwoord opslaan'}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-sm text-text-muted">
            <Link to="/login" className="text-primary hover:underline">
              ← Terug naar inloggen
            </Link>
          </p>
        </div>
      </AnimatedSection>
    </div>
  );
}
